<template>
  <div style="margin:20px">
    <FiPageRail title="Overlap Updates" />

    <div style="font-size: 22px; font-weight: bold; position: relative; margin: 30px" class="d-flex align-items-center justify-content-center">
      <a href="#" class="white-link" v-on:click.prevent="shift_detail_graph_date(-1)"><</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="options" :placeholder="details_toDate"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_detail_graph_date(1)">></a>
      &nbsp;&nbsp;<b-button v-on:click.prevent="load_data()">Search</b-button>
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;Filter Results&nbsp;<b-form-input style="width: 260px" v-model="text_filter" placeholder="booking id"/>
    </div>

    <div v-if="!loading">
      <div class="row" style="margin: 30px 10px 30px 10px; font-weight: bold; font-size: 22px;">
        <div class="col" style="border: 1px solid grey;">Total: {{summary.total_cnt}}</div>
        <div class="col" style="border: 1px solid grey;">Filtered: {{items.length}}</div>
      </div>
    </div>

    <div>
      <b-table :busy="loading" sticky-header="70vh" bordered :items="items" :fields="fields"
               :current-page="currentPage" :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="processed_time" slot-scope="row">
          {{row.item.processed_time | formatDateTimeSmall}}
        </template>

        <template slot="booking_id" slot-scope="row">
          <router-link v-if="row.item.booking_id" style="font-weight: bold;"
            :to="{ name: actualsRouteName, query: { booking_id: row.item.booking_id } }">
            {{ row.item.booking_id }}
          </router-link>
        </template>

        <template slot="trigger" slot-scope="row">
          {{ formatTrigger(row.item.trigger) }}
        </template>

        <template slot="changes" slot-scope="row">
          <b-button v-if="row.item.changes && row.item.changes.length" size="sm" variant="outline-primary"
            v-on:click.prevent="open_changes(row.item)">
            Show Changes ({{ row.item.changes.length }})
          </b-button>
          <span v-else class="text-muted">&mdash;</span>
        </template>

      </b-table>
      <b-row>
        <b-col>
          <b-pagination v-model="currentPage" :total-rows="items.length" :per-page="perPage" align="center"/>
        </b-col>
      </b-row>
    </div>

    <b-modal v-model="changes_modal_visible" :title="changes_modal_title" size="lg" ok-only ok-title="Close" scrollable>
      <div v-if="changes_modal_context.length" class="mb-3">
        <div class="font-weight-bold mb-1">Context</div>
        <div v-for="(ctx, i) in changes_modal_context" :key="'ctx-' + i" class="ctx-row">
          <span v-if="ctx.event"><b>Event:</b> {{ ctx.event }}</span>
          <span v-if="ctx.lane">&nbsp;|&nbsp;<b>Lane:</b> {{ ctx.lane }}</span>
          <span v-if="ctx.boundary_iso">&nbsp;|&nbsp;<b>Boundary:</b> {{ ctx.boundary_iso }}</span>
          <span v-if="ctx.boundary_source">&nbsp;|&nbsp;<b>Source:</b> {{ ctx.boundary_source }}</span>
          <span v-if="ctx.peer_booking_id">&nbsp;|&nbsp;<b>Peer:</b> {{ ctx.peer_booking_id }}</span>
          <span v-if="ctx.movement_id">&nbsp;|&nbsp;<b>Movement:</b> {{ ctx.movement_id }}</span>
        </div>
      </div>
      <div v-if="changes_modal_changes.length" class="chg-view">
        <div v-for="(c, i) in changes_modal_changes" :key="i" class="chg-row">
          <div class="chg-label">{{ c.label }}</div>
          <div class="chg-val">
            <span class="chg-old">{{ c.old }}</span>
            <span class="chg-arrow">&rarr;</span>
            <span class="chg-new">{{ c.new }}</span>
            <div v-if="c.sourceText" class="chg-source">{{ c.sourceText }}</div>
          </div>
        </div>
      </div>
      <div v-else class="text-muted">No field changes recorded.</div>
    </b-modal>
  </div>
</template>

<script>
import { get_tbn_overlap_updates_history } from '../networking/tbn_apis'
import { get_cm_overlap_updates_history } from '../networking/apis'
import { mapState } from 'vuex'
import moment from 'moment'
import { justDate } from '@/helpers/dates'

export default {
  name: 'overlap_updates_history',
  data() {
    return {
      text_filter: '',
      items: [],
      items_full: [],
      summary: {},
      loading: true,
      details_fromDate: justDate(moment().add(-1, 'days')),
      details_toDate: justDate(moment()),
      currentPage: 1,
      perPage: 100,
      changes_modal_visible: false,
      changes_modal_title: '',
      changes_modal_changes: [],
      changes_modal_context: [],
      fields: [
        { key: 'processed_time', label: 'Updated', sortable: true },
        { key: 'booking_id', label: 'Booking', sortable: true },
        { key: 'trigger', label: 'Trigger', sortable: true },
        { key: 'changes', label: 'Changes', sortable: false },
      ],
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(90, 'days'),
        maxDate: moment().add(90, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
        toolbarPlacement: 'top',
      },
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    isTbn() {
      return this.user && this.user.types && this.user.types[this.user.tenant] === 'TBN'
    },
    actualsRouteName() {
      return this.isTbn ? 'actuals_tbn' : 'actuals'
    },
  },
  async created() {
    this.load_data()
  },
  methods: {
    async load_data() {
      this.loading = true
      try {
        const start = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const end = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const fetcher = this.isTbn ? get_tbn_overlap_updates_history : get_cm_overlap_updates_history
        const { value: { data = {} } } = await fetcher(this.user.tenant, start, end)
        this.summary = data.summary || {}
        if (this.summary && this.summary.timezone)
          moment.tz.setDefault(this.summary.timezone)
        this.items = data.records || []
        this.items_full = data.records || []
        this.filter_results()
      } catch (e) {
        this.$emit('showAlert', 'error retrieving overlap update history', 'danger', 5000)
      }
      this.loading = false
    },
    shift_detail_graph_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
    },
    filter_results() {
      this.items = this.items_full || []
      if (this.text_filter !== '') {
        const val = this.text_filter
        this.items = this.items.filter(item => this.contains(String(item.booking_id), val))
      }
    },
    contains(val1, val2) {
      if (val1 != null)
        return val1.toLowerCase().indexOf(val2.toLowerCase()) !== -1
      return false
    },
    formatTrigger(trigger) {
      const labels = {
        periodic: 'Periodic job',
        inline_recheck: 'Inline re-check',
        manual_recheck: 'Manual re-check',
        planned_overlap: 'Planned overlap',
      }
      return labels[trigger] || trigger || ''
    },
    open_changes(item) {
      this.changes_modal_title = 'Booking ' + (item.booking_id || '') + ' @ ' + moment(item.processed_time).format('MM-DD-YYYY h:mm A')
      this.changes_modal_context = item.context || []
      this.changes_modal_changes = (item.changes || []).map(c => ({
        label: c.field,
        old: this.displayValue(c.before),
        new: this.displayValue(c.after),
        sourceText: this.formatSource(c.source),
      }))
      this.changes_modal_visible = true
    },
    displayValue(v) {
      if (v === null || v === undefined || v === '')
        return '(none)'
      if (typeof v === 'object')
        return JSON.stringify(v)
      return String(v)
    },
    formatSource(source) {
      if (!source)
        return ''
      const parts = []
      if (source.event) parts.push('event: ' + source.event)
      if (source.lane) parts.push('lane: ' + source.lane)
      if (source.boundary_source) parts.push('boundary: ' + source.boundary_source)
      if (source.peer_booking_id) parts.push('peer: ' + source.peer_booking_id)
      if (source.movement_id) parts.push('movement: ' + source.movement_id)
      if (source.driver_name) parts.push('driver: ' + source.driver_name)
      return parts.join(' | ')
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      },
    },
    text_filter() {
      this.filter_results()
    },
    details_fromDate(target_from_date) {
      this.details_fromDate = target_from_date
      if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
        this.details_toDate = this.details_fromDate
    },
  },
}
</script>

<style scoped>
div.row:nth-child(even) { background-color: #f2f2f2; }
tr:nth-child(even) { background-color: #f2f2f2; }
div.col { border: 1px solid grey; }
.table td { padding: .1rem !important; }
.chg-view { font-size: 13px; }
.chg-row { display: flex; gap: 14px; padding: 6px 0; align-items: baseline; border-bottom: 1px solid #f0f0f0; }
.chg-label { flex: 0 0 220px; font-weight: 600; color: #333; word-break: break-word; }
.chg-val { flex: 1; min-width: 0; word-break: break-word; }
.chg-old { color: #c62828; text-decoration: line-through; }
.chg-new { color: #2e7d32; }
.chg-arrow { margin: 0 8px; color: #888; }
.chg-source { font-size: 11px; color: #666; margin-top: 2px; }
.ctx-row { font-size: 13px; margin-bottom: 4px; }
</style>
